import { buildGraph, graphWarnings } from "./graph";
import {
    type LabelledChoice,
    linkingRuleProviderLabel,
    linkingRuleProviderValue,
    type StatusMeta,
    type WorkflowDefinition,
} from "./types";

/**
 * Every problem in a definition, in one list.
 *
 * The point is that these are all currently invisible. An unregistered action is logged and
 * skipped; an unusable condition means a rule that never fires; a shadowed rule is dead
 * configuration that reads as live; a status with no way out is the engine's "stuck workflow".
 * None of them show up in the admin, and each is only obvious once you know to look.
 *
 * Free of React so the detection rules are unit-tested directly.
 */

export type ProblemSeverity = "error" | "warning";

export interface Problem {
    severity: ProblemSeverity;
    /** Groups the list, and lets the UI link to the thing at fault. */
    transitionId: string | null;
    message: string;
}

export function collectProblems(
    definition: WorkflowDefinition,
    statuses: StatusMeta[],
    dataProviders: LabelledChoice[] = [],
): Problem[] {
    const problems: Problem[] = [];

    if (definition.transitions.length === 0) {
        problems.push({
            severity: "warning",
            transitionId: null,
            message:
                "This definition has no transitions, so nothing will ever happen to an event under it.",
        });
    }

    // _resolve_batch_linking logs a warning and skips the definition entirely when linking is
    // on with no rule, so this reads as configured and groups nothing.
    if (definition.autoEventLinking && definition.linkingRules.length === 0) {
        problems.push({
            severity: "error",
            transitionId: null,
            message:
                "Automatic event linking is on but no linking rule is configured, so the engine logs a warning and links nothing.",
        });
    }

    for (const rule of definition.linkingRules) {
        // Which rule a problem is about only matters once there is more than one. Named the way
        // the editor heads its card: by provider, or "All providers".
        const prefix =
            definition.linkingRules.length > 1
                ? `Event linking (${linkingRuleProviderLabel(dataProviders, linkingRuleProviderValue(rule))})`
                : "Event linking";

        // A rule with no grouping fields has nothing to group by.
        if (rule.groupingFields.length === 0) {
            problems.push({
                severity: "error",
                transitionId: null,
                message: `${prefix}: the linking rule has no grouping fields, so there is nothing to group events by.`,
            });
        }

        // Warnings from the rule's own validation — fields that resolve but match no data.
        for (const warning of rule.warnings) {
            problems.push({
                severity: "warning",
                transitionId: null,
                message: `${prefix}: ${warning}`,
            });
        }
    }

    for (const transition of definition.transitions) {
        const where = `${transition.fromStatus} → ${transition.toStatus}`;

        // A condition that cannot fire means the transition never runs. The server decides
        // usability, so this reads a flag rather than re-implementing the evaluator.
        if (transition.condition && !transition.condition.isUsable) {
            for (const problem of transition.condition.problems) {
                problems.push({
                    severity: "error",
                    transitionId: transition.id,
                    message: `${where}: its condition cannot fire — ${problem}`,
                });
            }
        }

        if (
            transition.transitionType === "MANUAL" &&
            transition.condition !== null
        ) {
            problems.push({
                severity: "error",
                transitionId: transition.id,
                message: `${where}: a manual transition's condition is never evaluated, so this guard does not apply.`,
            });
        }

        for (const config of transition.actionConfigs) {
            if (!config.isRegistered) {
                problems.push({
                    severity: "error",
                    transitionId: transition.id,
                    message: `${where}: the action "${config.actionName}" is no longer registered, so the engine logs it and moves on.`,
                });
            }

            if (config.rules.length === 0) {
                problems.push({
                    severity: "warning",
                    transitionId: transition.id,
                    message: `${where}: "${config.title}" has no rules, so it runs with no parameters.`,
                });
            }

            // First match wins, so an unconditional rule makes every rule after it dead.
            const alwaysMatchesAt = config.rules.findIndex(
                (rule) => rule.condition === null,
            );
            if (
                alwaysMatchesAt !== -1 &&
                alwaysMatchesAt < config.rules.length - 1
            ) {
                const shadowed = config.rules.length - alwaysMatchesAt - 1;
                problems.push({
                    severity: "error",
                    transitionId: transition.id,
                    message: `${where}: rule ${
                        alwaysMatchesAt + 1
                    } of "${config.title}" matches everything, so the ${shadowed} rule${
                        shadowed === 1 ? "" : "s"
                    } below it can never be reached.`,
                });
            }

            for (const [index, rule] of config.rules.entries()) {
                if (rule.condition && !rule.condition.isUsable) {
                    problems.push({
                        severity: "error",
                        transitionId: transition.id,
                        message: `${where}: rule ${index + 1} of "${
                            config.title
                        }" has a condition that cannot fire, so the rule never applies.`,
                    });
                }
            }
        }
    }

    // Structural problems come from the graph, so the overview and the graph cannot disagree.
    for (const message of graphWarnings(buildGraph(definition, statuses))) {
        problems.push({ severity: "warning", transitionId: null, message });
    }

    return problems;
}

export function countBySeverity(problems: Problem[]): {
    errors: number;
    warnings: number;
} {
    return {
        errors: problems.filter((problem) => problem.severity === "error")
            .length,
        warnings: problems.filter((problem) => problem.severity === "warning")
            .length,
    };
}
